import React from 'react';
import CustomerSignIn from './CustomerSignIn';
import ForgotPassword from './ForgotPassword';
import SetPasswordScreen from './SetPasswordScreen';
import { hasCustomerSession, PORTAL_BASENAME, signOut, subscribe } from './customerSession';
import { IDLE_SIGN_OUT_KEY } from '../IdleLogout';

export { PORTAL_BASENAME };

/// Everything the portal serves before there's a session. Deliberately not react-router: these are
/// four static screens that must render before any shell mounts, and pulling a Router in here would
/// mean two Routers on the page once ExternalApp mounts its own.
const SCREENS = {
  [`${PORTAL_BASENAME}/set-password`]: 'set-password',
  [`${PORTAL_BASENAME}/reset-password`]: 'reset-password',
  [`${PORTAL_BASENAME}/forgot-password`]: 'forgot-password',
};

function screenFor(pathname) {
  // Trailing slashes are normalized away so /portal/forgot-password/ isn't a different screen.
  return SCREENS[pathname.replace(/\/$/, '')] ?? null;
}

/**
 * The customer portal's gate: renders `children` (the customer shell) only once a session exists,
 * and otherwise one of the sign-in / invitation / reset screens.
 *
 * The internal equivalent is AuthGate.js, which is MSAL-driven. The two never both mount —
 * index.js picks one based on the URL — because a customer has no Entra account to silently renew
 * against and an internal user has no password here.
 */
export default function CustomerAuthGate({ children }) {
  const [signedIn, setSignedIn] = React.useState(hasCustomerSession);
  const [pathname, setPathname] = React.useState(() => window.location.pathname);
  const [notice, setNotice] = React.useState('');

  // The session module is the single source of truth: a refresh that fails deep inside apiClient
  // clears storage and notifies, and this is what turns that into the sign-in screen appearing.
  React.useEffect(() => subscribe(() => setSignedIn(hasCustomerSession())), []);

  React.useEffect(() => {
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Same flag AuthGate reads, so an idle sign-out explains itself instead of looking like a bug.
  React.useEffect(() => {
    try {
      if (sessionStorage.getItem(IDLE_SIGN_OUT_KEY) === '1') {
        sessionStorage.removeItem(IDLE_SIGN_OUT_KEY);
        setNotice('You were signed out after two hours of inactivity. Please sign in again.');
      }
    } catch {
      /* private mode */
    }
  }, []);

  const navigate = React.useCallback((to) => {
    window.history.pushState({}, '', to);
    setPathname(to);
  }, []);

  const goToSignIn = React.useCallback(() => navigate(PORTAL_BASENAME), [navigate]);

  const screen = screenFor(pathname);
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  // Checked before `signedIn`: someone with a live session who follows a reset link must still be
  // able to use it, and setting a password ends every session anyway.
  if (screen === 'set-password' || screen === 'reset-password') {
    return (
      <SetPasswordScreen
        kind={screen === 'set-password' ? 'invite' : 'reset'}
        token={token}
        onDone={async () => {
          // The API revoked every session for this account when the password changed, so whatever
          // is in this tab's storage is already dead — drop it rather than letting the shell mount
          // with tokens that will 401 on first use.
          await signOut();
          setNotice('');
          goToSignIn();
        }}
        onRequestNewLink={() => navigate(`${PORTAL_BASENAME}/forgot-password`)}
      />
    );
  }

  if (screen === 'forgot-password') {
    return <ForgotPassword onBack={goToSignIn} />;
  }

  if (signedIn) return children;

  return (
    <CustomerSignIn
      notice={notice}
      onSignedIn={() => {
        setNotice('');
        // Land on the portal root rather than wherever the sign-in happened to be, so the shell's
        // router starts at a route it actually has.
        if (pathname !== PORTAL_BASENAME) navigate(PORTAL_BASENAME);
      }}
      onForgotPassword={() => navigate(`${PORTAL_BASENAME}/forgot-password`)}
    />
  );
}
